import { createApi } from '@reduxjs/toolkit/query/react';

import { adaptMeetingTips } from '@api/adapters/meetingTips';
import { adaptPostRaceComments } from '@api/adapters/postRaceComments';
import { adaptPressBoard } from '@api/adapters/pressBoard';
import { adaptRaceTips } from '@api/adapters/raceTips';
import { TAG_EDITORIAL_CONTENTS } from '@config/tags';
import {
  POST_RACE_COMMENTS,
  RACE_TIPS,
  MEETING_TIPS,
  PRESS_BOARD,
  REGIONAL_PRESS_BOARD,
} from '@config/urls';

import { baseQuery } from './baseQuery';

import type { ApiMeetingTips, MeetingTips } from '@api/types/meetingTips';
import type { ApiPostRaceComments, PostRaceComments } from '@api/types/postRaceComments';
import type { ApiPressBoard, PressBoard } from '@api/types/pressBoard';
import type { ApiRaceTips, RaceTips } from '@api/types/raceTips';
import type { RegionalPressBoard } from '@api/types/regionalPressBoard';
import type { Lang } from '@assets/locales';

type PostRaceCommentsArgs = {
  date: string;
  racecourseCode: string;
  raceNumber: number;
  language: Lang;
};

type RaceTipsArgs = {
  date: string;
  racecourseCode: string;
  raceNumber: number;
  language: Lang;
};

type MeetingTipsArgs = {
  date: string;
  racecourseCode: string;
  language: Lang;
};

type PressBoardArgs = {
  date: string;
  racecourseCode: string;
  raceNumber: number;
  language: Lang;
};

type RegionalPressBoardArgs = {
  date: string;
  racecourseCode: string;
  raceNumber: number;
  language: Lang;
};

export const editorialContentsApi = createApi({
  reducerPath: 'editorialContentsApi',
  tagTypes: [TAG_EDITORIAL_CONTENTS],
  baseQuery,
  refetchOnFocus: true,
  endpoints: builder => ({
    getPostRaceComments: builder.query<PostRaceComments, PostRaceCommentsArgs>({
      query: ({ date, racecourseCode, raceNumber }) => ({
        url: `${POST_RACE_COMMENTS}/${date}/${racecourseCode}/${raceNumber}`,
      }),
      transformResponse: (response: ApiPostRaceComments): PostRaceComments =>
        adaptPostRaceComments(response),
    }),
    getRaceTips: builder.query<RaceTips, RaceTipsArgs>({
      query: ({ date, racecourseCode, raceNumber }) => ({
        url: `${RACE_TIPS}/${date}/${racecourseCode}/${raceNumber}`,
      }),
      transformResponse: (response: ApiRaceTips): RaceTips => adaptRaceTips(response),
    }),
    getMeetingTips: builder.query<MeetingTips, MeetingTipsArgs>({
      query: ({ date, racecourseCode }) => ({
        url: `${MEETING_TIPS}/${date}/${racecourseCode}`,
      }),
      transformResponse: (response: ApiMeetingTips): MeetingTips => adaptMeetingTips(response),
    }),
    getPressBoard: builder.query<PressBoard, PressBoardArgs>({
      query: ({ date, racecourseCode, raceNumber, language }) => ({
        url: `${PRESS_BOARD}/${date}/${racecourseCode}/${raceNumber}`,
        params: { language },
      }),
      transformResponse: (response: ApiPressBoard): PressBoard => adaptPressBoard(response),
    }),
    getRegionalPressBoard: builder.query<RegionalPressBoard, RegionalPressBoardArgs>({
      query: ({ date, racecourseCode, raceNumber, language }) => ({
        url: `${REGIONAL_PRESS_BOARD}/${date}/${racecourseCode}/${raceNumber}`,
        params: { language },
      }),
    }),
  }),
});

export const {
  useGetPostRaceCommentsQuery,
  useGetRaceTipsQuery,
  useGetMeetingTipsQuery,
  useGetPressBoardQuery,
  useGetRegionalPressBoardQuery,
  useLazyGetRaceTipsQuery,
  useLazyGetPressBoardQuery,
  useLazyGetRegionalPressBoardQuery,
} = editorialContentsApi;
